import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { brl, statusLabel, statusColor, formatDate } from "@/lib/format";
import { CheckCircle2, Copy, Clock } from "lucide-react";
import { toast } from "sonner";

export default function OrderConfirmation() {
  const { id } = useParams();
  const [order, setOrder] = useState(null);

  const load = () => api.get(`/orders/${id}`).then((r) => setOrder(r.data));
  useEffect(() => { load(); }, [id]);

  const copyPix = () => {
    navigator.clipboard.writeText(order.pix_code);
    toast.success("Código PIX copiado!");
  };

  const simulate = async () => {
    await api.post(`/orders/${id}/simulate-payment`);
    toast.success("Pagamento aprovado (DEMO)! 🎉");
    load();
  };

  if (!order) return <div className="max-w-3xl mx-auto px-4 py-20 text-center text-[#8C7A80]">Carregando...</div>;

  return (
    <div className="max-w-3xl mx-auto px-4 py-10" data-testid="order-confirmation">
      <div className="text-center mb-8">
        <div className="w-16 h-16 bg-[#7DBE9E] text-white rounded-full flex items-center justify-center mx-auto mb-4">
          <CheckCircle2 className="w-10 h-10" />
        </div>
        <h1 className="font-serif text-3xl text-[#4A3B40]">Pedido realizado!</h1>
        <p className="text-[#8C7A80] mt-2">Obrigado, {(order.customer_name || "").split(" ")[0]}! Enviamos os detalhes para <strong>{order.customer_email}</strong>.</p>
        <p className="text-xs text-[#8C7A80] mt-1">Pedido <strong>{order.order_number}</strong> em {formatDate(order.created_at)}</p>
      </div>

      <div className={`inline-flex items-center gap-2 text-sm px-3 py-1 rounded-full border ${statusColor(order.status)} mb-6`}>
        <Clock className="w-4 h-4" /> Status: {statusLabel(order.status)}
      </div>

      {order.payment_method === "pix" && order.status === "aguardando_pagamento" && (
        <div className="bg-white rounded-2xl border-2 border-dashed border-[#E8A0B4] p-6 mb-6 text-center" data-testid="pix-section">
          <h3 className="font-serif text-xl text-[#4A3B40]">Pague com PIX</h3>
          <p className="text-sm text-[#8C7A80] mt-1">Escaneie o QR Code ou copie o código abaixo</p>
          <img src={order.pix_qr} alt="QR Code PIX" className="w-56 h-56 mx-auto my-4 rounded-xl" data-testid="pix-qr-image" />
          <div className="bg-[#FFF9FA] rounded-xl p-3 text-xs text-[#4A3B40] font-mono break-all mb-3">
            {order.pix_code}
          </div>
          <button onClick={copyPix} className="btn-lara-outline mx-auto" data-testid="pix-copy">
            <Copy className="w-4 h-4" /> Copiar código PIX
          </button>
          <button onClick={simulate} className="btn-lara mt-3 mx-auto" data-testid="pix-simulate">
            Simular pagamento (DEMO)
          </button>
        </div>
      )}

      <div className="bg-white rounded-2xl border border-[#F9E1E8] p-5 mb-4">
        <h3 className="font-serif text-lg text-[#4A3B40] mb-3">Itens do pedido</h3>
        {order.items.map((it, i) => (
          <div key={i} className="flex gap-3 py-2 border-b border-[#F9E1E8] last:border-0">
            <img src={it.image} className="w-14 h-14 rounded-lg object-cover" alt="" />
            <div className="flex-1">
              <p className="text-sm text-[#4A3B40]">{it.name}</p>
              <p className="text-xs text-[#8C7A80]">{it.quantity}x {brl(it.unit_price)}</p>
            </div>
            <p className="font-semibold text-[#C9708B]">{brl(it.line_total)}</p>
          </div>
        ))}
        <div className="pt-3 space-y-1 text-sm">
          <div className="flex justify-between"><span className="text-[#8C7A80]">Subtotal</span><span>{brl(order.subtotal)}</span></div>
          {order.discount > 0 && <div className="flex justify-between text-[#7DBE9E]"><span>Cupom {order.coupon_code}</span><span>- {brl(order.discount)}</span></div>}
          {order.pix_discount > 0 && <div className="flex justify-between text-[#7DBE9E]"><span>Desconto PIX</span><span>- {brl(order.pix_discount)}</span></div>}
          <div className="flex justify-between"><span className="text-[#8C7A80]">Frete</span><span>{order.shipping_cost===0?'Grátis':brl(order.shipping_cost)}</span></div>
          <div className="flex justify-between pt-2 border-t border-[#F9E1E8] font-bold text-base"><span>Total</span><span className="text-[#C9708B]">{brl(order.total)}</span></div>
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-[#F9E1E8] p-5 mb-6">
        <h3 className="font-serif text-lg text-[#4A3B40] mb-2">Endereço de entrega</h3>
        <p className="text-sm text-[#4A3B40]">{order.address.street}, {order.address.number}{order.address.complement?` - ${order.address.complement}`:""}</p>
        <p className="text-sm text-[#8C7A80]">{order.address.neighborhood}, {order.address.city} - {order.address.state}</p>
        <p className="text-sm text-[#8C7A80]">CEP: {order.address.cep}</p>
      </div>

      <div className="flex gap-3 justify-center">
        <Link to="/minha-conta" className="btn-lara-outline">Meus pedidos</Link>
        <Link to="/" className="btn-lara">Continuar comprando</Link>
      </div>
    </div>
  );
}
